"use client";
import React from "react";
import { cva, type VariantProps } from "class-variance-authority";
import { cn } from "@/lib/utils/cn";

const phoneVariants = cva(
  "relative mx-auto border-gray bg-gray border-12 rounded-[2.5rem] shrink-0 [--base:240px] w-[var(--base)] h-[calc(var(--base)*2)] border-10 rounded-4xl [--button-offset:12px] [--inner-radius:1.5rem]",
  {
    variants: {
      size: {
        sm: "",
        md: "lg:[--base:300px] lg:border-12 lg:rounded-[2.5rem] lg:[--button-offset:15px] lg:[--inner-radius:2rem]",
        lg: "lg:[--base:360px] lg:border-12 lg:rounded-[2.5rem] lg:[--button-offset:15px] lg:[--inner-radius:2rem]",
      },
    },
    defaultVariants: {
      size: "md",
    },
  }
);

export interface PhoneMockupProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof phoneVariants> {
  children?: React.ReactNode;
}

/**
 * A reusable Phone Mockup component for displaying images, videos, or other content.
 * Follows the smartphone mockup design with dynamic content slot.
 */
export const PhoneMockup: React.FC<PhoneMockupProps> = ({
  children,
  size,
  className,
  ...props
}) => {
  const [isLocked, setIsLocked] = React.useState(false);

  return (
    <div className={cn(phoneVariants({ size }), className)} {...props}>
      {/* Side buttons / Hardware details */}
      <div className="absolute top-[72px] h-[32px] w-[3px] rounded-s-lg bg-gray -start-(--button-offset)" />
      <div className="absolute top-[124px] h-[46px] w-[3px] rounded-s-lg bg-gray -start-(--button-offset)" />
      <div className="absolute top-[178px] h-[46px] w-[3px] rounded-s-lg bg-gray -start-(--button-offset)" />
      <button
        onClick={() => setIsLocked(!isLocked)}
        className="absolute top-[142px] h-16 w-[3px] rounded-e-lg bg-gray -end-(--button-offset) transition-colors active:scale-95"
        aria-label={isLocked ? "Unlock side button" : "Lock side button"}
      />
      {/* Screen Content Wrapper */}
      <div
        className={cn(
          "h-full w-full overflow-hidden transition-colors duration-500 rounded-(--inner-radius)",
          isLocked ? "bg-black" : "bg-white dark:bg-gray"
        )}
      >
        <div
          className={cn(
            "relative h-full w-full aspect-9/21 transition-opacity duration-500",
            isLocked ? "opacity-0" : "opacity-100"
          )}
        >
          {children}
        </div>
      </div>
    </div>
  );
};
